/**
 * The on-device gallery.
 *
 * Finished photographs are kept in IndexedDB, as blobs, on this device only.
 * There is no upload path anywhere in this file or anywhere it is called from —
 * that is the product, not an oversight.
 *
 * Each entry keeps the before as well as the after, because the before is what
 * makes the after legible, and because a repair somebody dislikes is worth
 * being able to go back from.
 */

const DB_NAME = 'second-person';
const DB_VERSION = 1;
const STORE = 'shots';

export interface Shot {
  id: string;
  takenAt: number;
  after: Blob;
  before: Blob;
  /**
   * A small copy for the grid. Optional so that shots written by an earlier
   * build still open; the grid falls back to the full photograph.
   */
  thumb?: Blob;
  width: number;
  height: number;
  tiltDegrees: number;
  /** Phones taken out. Absent on shots kept by the 1.0 build. */
  removed?: number;
  /** Phones left in because they hid the face. */
  refused?: number;
  /** Brush strokes applied on top. */
  strokes?: number;
  /** Where the frame came from. */
  source?: 'camera' | 'photo' | 'clip';
  /** 1.0 build only: share of the hole recovered from earlier frames. */
  recoveredPct?: number;
  /** 1.0 build only: which buffered frame became the plate. */
  provenance?: string;
}

/** One line on what happened to a photograph, for the grid and the viewer. */
export function describeShot(shot: Pick<Shot, 'removed' | 'refused' | 'strokes' | 'recoveredPct'>): string {
  if (shot.removed === undefined) return shot.recoveredPct !== undefined ? `${shot.recoveredPct}% recovered` : 'Kept';
  const parts: string[] = [];
  if (shot.removed > 0) parts.push(shot.removed === 1 ? 'Phone removed' : `${shot.removed} phones removed`);
  if (shot.refused) parts.push('phone over the face left in');
  if (shot.strokes) parts.push(`${shot.strokes} brush ${shot.strokes === 1 ? 'stroke' : 'strokes'}`);
  if (parts.length === 0) parts.push('No phone in the picture');
  const line = parts.join(', ');
  return line.charAt(0).toUpperCase() + line.slice(1);
}

export interface ShotSummary extends Omit<Shot, 'after' | 'before' | 'thumb'> {
  bytes: number;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('takenAt', 'takenAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('The gallery could not be opened.'));
    req.onblocked = () => reject(new Error('The gallery is open in another tab.'));
  });
}

function run<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = fn(tx.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('The gallery could not be read.'));
        tx.oncomplete = () => db.close();
      }),
  );
}

/** True when this browser will let us keep anything at all. */
export function isGalleryAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

export async function saveShot(shot: Omit<Shot, 'id'>): Promise<string> {
  // Sortable and unique without a dependency: the timestamp keeps the natural
  // order, the suffix separates two shots taken in the same millisecond.
  const id = `${shot.takenAt.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  await run('readwrite', (store) => store.put({ ...shot, id }));
  return id;
}

/** Every shot, blobs included, newest first — what the gallery grid renders from. */
export async function allShots(): Promise<Shot[]> {
  const shots = await run<Shot[]>('readonly', (store) => store.getAll());
  return shots.sort((a, b) => b.takenAt - a.takenAt);
}

/** The same list without the blobs, for counting and for the storage line. */
export async function listShots(): Promise<ShotSummary[]> {
  const shots = await allShots();
  return shots.map(({ after, before, thumb, ...rest }) => ({
    ...rest,
    bytes: after.size + before.size + (thumb?.size ?? 0),
  }));
}

export function getShot(id: string): Promise<Shot | undefined> {
  return run<Shot | undefined>('readonly', (store) => store.get(id));
}

export function deleteShot(id: string): Promise<void> {
  return run<undefined>('readwrite', (store) => store.delete(id)).then(() => undefined);
}

export async function clearGallery(): Promise<void> {
  await run<undefined>('readwrite', (store) => store.clear());
}

/** Bytes held and, where the browser will say, bytes available. */
export async function galleryUsage(): Promise<{ used: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { used: usage, quota };
}

/**
 * Ask the browser not to evict the gallery under storage pressure.
 * Chrome grants this silently for installed apps; Safari ignores it.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
